import moment from 'moment'
import debounce from '@/utils/debounce'
import { MODULE_NAME, ACTIONS } from '@/store/locations/constants'
import { MODULE_NAME as MODULE_NAME_FILTER, GETTERS as GETTERS_FILTER } from '@/store/filters/constants'
import { mapActions, mapGetters, mapState } from 'vuex'
import authService, { USER_ROLES } from '@/api/authService'
import layerService from '@/api/layerService'
import customFormatters from '@/filters/customFormatters'
import PointRadiusLayer from '@/api/PointRadiusLayer'
import stringCompare from '@/utils/stringCompare'
import i18n from '@/i18n'

const DEBOUNCE_TIME = 500

const ZONE_COLOR = [22, 150, 157, 25]
const ZONE_HOVER_COLOR = [22, 150, 157, 75]
const ZONE_SELECTED_COLOR = [22, 150, 157, 140]
const ZONE_BORDER_COLOR = [22, 150, 157, 255]

function getZoneColor (id, hover, selection) {
  const hovered = id === hover
  const allSelected = selection.length === 1 && selection[0].id === 'all'
  const selected = allSelected || selection.some(s => s.id === id)
  if (selected) {
    return ZONE_SELECTED_COLOR
  }
  if (hovered) {
    return ZONE_HOVER_COLOR
  }
  return ZONE_COLOR
}

export default {
  name: 'ExploreSelection',
  mixins: [customFormatters],
  components: {
    ZipcodeSelector: () => import(/* webpackChunkName: "ZipcodeSelector" */ '@/components/zipcode-selector/ZipcodeSelector.vue'),
    TagListSelector: () => import(/* webpackChunkName: "TagListSelector" */ '@/components/tag-list-selector/TagListSelector.vue')
  },
  props: {},
  data () {
    return {
      authData: authService.authData,
      startDate: moment().startOf('month').toDate(),
      finishDate: moment().endOf('month').toDate(),
      boxSelectionEnabled: false,
      dragStart: null,
      dragCurrent: null,
      selectedTab: null,
      selectedZones: [],
      selectedGrids: [],
      hoveredZoneId: null,
      hoveredGridId: null,
      selectedArea: null,
      zipcodeLayerId: 'explore_zipcodes',
      gridLayerId: 'explore_grids',
      areaLayerId: 'explore_area',
      rangeMin: 30,
      rangeMax: 5000,
      selectedModelId: null,
      selectedModelName: null
    }
  },
  computed: {
    ...mapState(MODULE_NAME, ['zones', 'grids']),
    ...mapGetters(MODULE_NAME_FILTER, {
      filters: GETTERS_FILTER.GET_ROUTE_FILTERS
    }),
    selectionReady () {
      return this.selectedGeometries.length > 0
    },
    isDirector () {
      return this.authData.user.role === USER_ROLES.DIRECTOR
    },
    canShowTabs () {
      return this.zones.query && this.grids.query
    },
    hasBoxSelection () {
      return this.dragStart && this.dragCurrent
    },
    boxSelectionStyles () {
      if (!this.hasBoxSelection) {
        return { display: 'none' }
      }
      const minX = Math.min(this.dragStart.x, this.dragCurrent.x)
      const minY = Math.min(this.dragStart.y, this.dragCurrent.y)
      const maxX = Math.max(this.dragStart.x, this.dragCurrent.x)
      const maxY = Math.max(this.dragStart.y, this.dragCurrent.y)
      return {
        display: 'block',
        transform: `translate(${minX}px, ${minY}px)`,
        width: `${maxX - minX - 12}px`,
        height: `${maxY - minY - 12}px`
      }
    },
    selectedGeometries () {
      if (this.selectedTab === 'postalcode') {
        return this.selectedZones
      }
      if (this.selectedTab === 'grid') {
        return this.selectedGrids
      }
      if (this.selectedTab === 'area') {
        return [this.selectedArea].filter(Boolean)
      }
      return []
    },
    selectedAreaLabel () {
      const { lng, lat } = this.selectedArea
      return `${lng}, ${lat}`
    },
    shopsFilter () {
      return this.filters.find(f => f.key === 'shop_id')
    },
    shopsFilterValue () {
      return this.shopsFilter && this.shopsFilter.value.length ? this.shopsFilter.value : 'all'
    }
  },
  watch: {
    selectedZones () {
      this.updatePostalCodeLayer()
    },
    selectedGrids () {
      this.updateGridLayer()
    },
    selectedArea () {
      this.drawAreaLayer()
    },
    hoveredZoneId () {
      this.updatePostalCodeLayer()
    },
    hoveredGridId () {
      this.updateGridLayer()
    },
    shopsFilterValue () {
      this.zipcodeSearch('')
      this.gridSearch('')
    }
  },
  mounted () {
    this._zipcodeSearch = debounce(this.zipcodeSearch, DEBOUNCE_TIME)
    this._gridSearch = debounce(this.gridSearch, DEBOUNCE_TIME)
    this.zipcodeSearch('')
    this.gridSearch('')
    layerService.removeLayer(this.zipcodeLayerId)
    layerService.removeLayer(this.gridLayerId)
    layerService.removeLayer(this.areaLayerId)
  },
  beforeDestroy () {
    if (layerService.deckInstance) {
      this.disableBoxSelection()
      this.disableAreaClick()
    }
  },
  methods: {
    ...mapActions(MODULE_NAME, [ ACTIONS.FETCH_ZONES, ACTIONS.FETCH_GRIDS ]),
    emitAnalysisModel () {
      this.$emit('toggleAnalysis', {
        id: this.selectedModelId,
        name: this.selectedModelName,
        type: this.selectedTab,
        geometries: this.selectedGeometries.sort((a, b) => stringCompare(a.name, b.name)),
        startDate: this.startDate,
        finishDate: this.finishDate
      })
    },
    onDatepickerChange (dates) {
      const [start, finish] = dates
      this.startDate = start
      this.finishDate = finish
    },
    onTabChange ({ id }) {
      this.selectedTab = id
      this.setupMap()
    },
    onZonesSelected (zones) {
      this.selectedZones = zones
    },
    onZoneGroupSelected ({ regions, name, id }) {
      this.selectedModelId = id
      this.selectedModelName = name
      this.selectedZones = this.selectedZones.concat(regions)
    },
    slowZipcodeSearch (query) { this._zipcodeSearch(query) },
    zipcodeSearch (query) {
      this[ACTIONS.FETCH_ZONES]({ reset: true, page_size: 10, in_shop_influence_area: this.shopsFilterValue, q: query, query: Number(!this.zones.query) })
    },
    onGridSelected (opt) {
      if (this.gridIsSelected(opt)) {
        this.selectedGrids = this.selectedGrids.filter(g => g.id !== opt.id)
      } else {
        this.selectedGrids = this.selectedGrids.concat(opt)
      }
    },
    clearGrids () {
      this.selectedGrids = []
    },
    gridIsSelected (opt) {
      return this.selectedGrids.some(g => g.id === opt.id)
    },
    slowGridSearch (query) { this._gridSearch(query) },
    gridSearch (query) {
      this[ACTIONS.FETCH_GRIDS]({ reset: true, page_size: 10, in_shop_influence_area: this.shopsFilterValue, q: query, query: Number(!this.grids.query) })
    },
    updateScrollbar () {
      window.setTimeout(() => {
        if (this.$refs.scrollbar) this.$refs.scrollbar.update()
      }, 250)
    },
    enableBoxSelection () {
      layerService.deckInstance.setProps({
        controller: false,
        onDragStart: ({ x, y }) => {
          this.dragStart = { x, y }
        },
        onDrag: ({ x, y }) => {
          this.dragCurrent = { x, y }
        },
        onDragEnd: ({ x, y }) => {
          if (!this.hasBoxSelection) {
            return
          }
          const minX = Math.min(this.dragStart.x, x)
          const minY = Math.min(this.dragStart.y, y)
          const maxX = Math.max(this.dragStart.x, x)
          const maxY = Math.max(this.dragStart.y, y)
          const mapping = {
            postalcode: this.zipcodeLayerId,
            grid: this.gridLayerId,
            area: this.areaLayerId
          }

          const selection = layerService.deckInstance.pickObjects({
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
            layerIds: [mapping[this.selectedTab]]
          })
            .filter(info => info.object && (
              !this.selectedGeometries.some(g => g.id === info.object.properties.cartodb_id)
            ))
            .map(info => ({
              id: info.object.properties.cartodb_id,
              name: info.object.properties.name
            }))

          if (this.selectedTab === 'postalcode') {
            this.selectedZones = this.selectedZones.concat(selection)
          }
          if (this.selectedTab === 'grid') {
            this.selectedGrids = this.selectedGrids.concat(selection)
          }

          this.dragStart = null
          this.dragCurrent = null
        }
      })
    },
    disableBoxSelection () {
      layerService.deckInstance.setProps({
        controller: true,
        onDragStart: null,
        onDrag: null,
        onDragEnd: null
      })
    },
    toggleBoxSelection ({ state }) {
      this.boxSelectionEnabled = state
      if (state) {
        this.enableBoxSelection()
      } else {
        this.disableBoxSelection()
      }
    },
    async setupMap () {
      if (this.selectedTab === 'postalcode' && !layerService.hasLayer(this.zipcodeLayerId)) {
        try {
          await this.createPostalCodeLayer()
        } catch (err) {
          // eslint-disable-next-line
          console.error('[explore-selection.js] Error creating postalcode layer', err)
        }
      }
      if (this.selectedTab === 'grid' && !layerService.hasLayer(this.gridLayerId)) {
        try {
          await this.createGridLayer()
        } catch (err) {
          // eslint-disable-next-line
          console.error('[explore-selection.js] Error creating grids layer', err)
        }
      }
      if (this.selectedTab === 'area' && !layerService.hasLayer(this.areaLayerId)) {
        this.drawAreaLayer()
        this.setupAreaClick()
      }
      if (this.selectedTab !== 'area') {
        this.disableAreaClick()
      }
      this.toggleLayers()
    },
    toggleLayers () {
      const mapping = {
        postalcode: this.zipcodeLayerId,
        grid: this.gridLayerId,
        area: this.areaLayerId
      }
      for (const key in mapping) {
        if (this.selectedTab === key) {
          layerService.showLayer(mapping[key])
        } else {
          layerService.hideLayer(mapping[key])
        }
      }
    },
    createPostalCodeLayer () {
      return layerService.addSQLLayer({
        id: this.zipcodeLayerId,
        sql: this.zones.query,
        visible: false,
        pickable: true,
        zIndex: -2,
        getLineColor: ZONE_BORDER_COLOR,
        getFillColor: (object) => {
          const id = object.properties.cartodb_id
          return getZoneColor(id, this.hoveredZoneId, this.selectedZones)
        },
        updateTriggers: {
          getFillColor: [this.hoveredZoneId, this.selectedZones]
        },
        onHover: ({ object }) => {
          this.hoveredZoneId = (object && object.properties.cartodb_id) || null
        },
        onClick: ({ object }) => {
          if (!object) {
            return
          }
          const selected = this.selectedZones.some(s => s.id === object.properties.cartodb_id)
          if (selected) {
            this.selectedZones = this.selectedZones.filter(s => s.id !== object.properties.cartodb_id)
          } else {
            this.selectedZones = this.selectedZones.concat({
              id: object.properties.cartodb_id,
              name: object.properties.name
            })
          }
        },
        getTooltip ({ object }) {
          const text = `${i18n.t('ZIPCODE_SHORTER')} ${object.properties.name}`
          const style = {
            'background': 'var(--colorNegative)',
            color: 'var(--colorHigh)'
          }
          return { text, style, className: 'map-popup-white' }
        }
      })
    },
    updatePostalCodeLayer () {
      layerService.updateLayer(this.zipcodeLayerId, {
        updateTriggers: {
          getFillColor: [this.hoveredZoneId, this.selectedZones]
        }
      })
    },
    createGridLayer () {
      return layerService.addSQLLayer({
        id: this.gridLayerId,
        sql: this.grids.query,
        visible: false,
        pickable: true,
        zIndex: -2,
        getLineColor: ZONE_BORDER_COLOR,
        getFillColor: (object) => {
          const id = object.properties.cartodb_id
          return getZoneColor(id, this.hoveredGridId, this.selectedGrids)
        },
        updateTriggers: {
          getFillColor: [this.hoveredGridId, this.selectedGrids]
        },
        onHover: ({ object }) => {
          this.hoveredGridId = (object && object.properties.cartodb_id) || null
        },
        onClick: ({ object }) => {
          if (!object) {
            return
          }
          const selected = this.selectedGrids.some(s => s.id === object.properties.cartodb_id)
          if (selected) {
            this.selectedGrids = this.selectedGrids.filter(s => s.id !== object.properties.cartodb_id)
          } else {
            this.selectedGrids = this.selectedGrids.concat({
              id: object.properties.cartodb_id,
              name: object.properties.name
            })
          }
        },
        getTooltip ({ object }) {
          const text = `${i18n.tc('GRID', 1)} ${object.properties.name}`
          const style = {
            'background': 'var(--colorNegative)',
            color: 'var(--colorHigh)'
          }
          return { text, style, className: 'map-popup-white' }
        }
      })
    },
    updateGridLayer () {
      layerService.updateLayer(this.gridLayerId, {
        updateTriggers: {
          getFillColor: [this.hoveredGridId, this.selectedGrids]
        }
      })
    },
    drawAreaLayer () {
      const iconUrl = require('@/assets/icons/map/icon-marker.svg')
      const layer = {
        id: this.areaLayerId,
        layerType: PointRadiusLayer,
        data: this.selectedArea ? [this.selectedArea] : [],
        circleLineWidthMinPixels: 2,
        getPosition: d => [d.lng, d.lat],
        getRadius: d => d.radius,
        getIcon: () => ({
          width: 32,
          height: 32,
          url: iconUrl,
          anchorY: 32
        })
      }
      layerService.addLayer(layer)
    },
    setupAreaClick () {
      layerService.deckInstance.setProps({
        onClick: ({ lngLat }) => {
          this.selectedArea = {
            lng: lngLat[0],
            lat: lngLat[1],
            radius: this.selectedArea ? this.selectedArea.radius : 500,
            name: lngLat.join(', ')
          }
        }
      })
    },
    disableAreaClick () {
      layerService.deckInstance.setProps({ onClick: null })
    },
    clearArea () {
      this.selectedArea = null
    },
    onAreaRadiusChange (value) {
      this.selectedArea.radius = value
      this.drawAreaLayer()
    }
  }
}
